import { AVATAR_REALTIME_URL } from "../../config/apiConfig";
const API_URL = AVATAR_REALTIME_URL;

let peerConnection = null;
let streamId = null;
let sessionId = null; // Store sessionId if provided by API
let isConnecting = false;
/** Shared in-flight connect promise so concurrent callers wait instead of no-op. */
let pendingConnectPromise = null;
let iceCandidateQueue = [];
let sdpSubmitted = false;
let iceGatheringComplete = false;
let iceCandidateFailureCount = 0; // Track consecutive ICE candidate failures
let maxIceCandidateFailures = 3; // Stop sending after 3 consecutive failures
let shouldStopSendingIceCandidates = false; // Flag to stop sending ICE candidates

/**
 * Create or reuse WebRTC connection
 */
export async function connectAvatar(videoRef) {
  if (peerConnection && peerConnection.connectionState === "connected") {
    return { peerConnection, streamId };
  }

  if (pendingConnectPromise) {
    return pendingConnectPromise;
  }

  pendingConnectPromise = connectAvatarInternal(videoRef);
  try {
    return await pendingConnectPromise;
  } finally {
    pendingConnectPromise = null;
  }
}

async function connectAvatarInternal(videoRef) {
  try {
    if (peerConnection && peerConnection.connectionState === "connected") {
      return { peerConnection, streamId };
    }

    if (isConnecting) {
      throw new Error("Avatar connection already in progress");
    }
    isConnecting = true;

    /** 1. Create Stream */
    let response;
    try {
      response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "create_agent_stream" }),
      });
    } catch (err) {
      console.error("❌ Network error creating stream:", err);
      throw new Error("Create stream API failed: " + err.message);
    }

    // Check if response is OK before parsing
    if (!response.ok) {
      let errorText = "";
      try {
        const errorData = await response.json();
        errorText = errorData.error || errorData.message || JSON.stringify(errorData);
      } catch {
        errorText = await response.text().catch(() => `HTTP ${response.status}`);
      }
      console.error("❌ Create stream API error:", response.status, errorText);
      throw new Error(`Create stream failed: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    const { id, sessionId: apiSessionId, offer, ice_servers } = responseData;
    
    if (!id || !offer) {
      console.error("❌ Invalid response from create stream API:", responseData);
      throw new Error("Invalid response: missing id or offer");
    }
    
    // Use sessionId if provided, otherwise fall back to id
    streamId = id;
    sessionId = apiSessionId || id; // Store sessionId for API calls if provided
    
    // Reset ICE candidate failure tracking for new connection
    iceCandidateFailureCount = 0;
    shouldStopSendingIceCandidates = false;

    /** Create Peer Connection with validated ICE servers */
    try {
      // Validate and add fallback STUN servers if ice_servers is missing or invalid
      let iceServers = ice_servers;
      if (!iceServers || !Array.isArray(iceServers) || iceServers.length === 0) {
        console.warn("⚠️ No ice_servers provided, using fallback STUN servers");
        iceServers = [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ];
      } else {
        // Validate ice_servers format
        iceServers = iceServers.map((server) => {
          if (typeof server === "string") {
            return { urls: server };
          }
          if (server && server.urls) {
            return server;
          }
          console.warn("⚠️ Invalid ICE server format:", server);
          return null;
        }).filter(Boolean);
        
        // Add fallback if all servers were invalid
        if (iceServers.length === 0) {
          console.warn("⚠️ All ice_servers invalid, using fallback STUN servers");
          iceServers = [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
          ];
        }
      }
      
      // iOS Safari WebRTC configuration
      // Note: Modern browsers (including iOS Safari 11+) use unified-plan SDP by default
      // No need to explicitly set sdpSemantics as it's deprecated and unified-plan is the only option
      const pcConfig = {
        iceServers: iceServers,
      };
      
      peerConnection = new RTCPeerConnection(pcConfig);
    } catch (err) {
      throw new Error("Failed to create RTCPeerConnection");
    }
    
    /** Attach video stream */
    peerConnection.ontrack = (event) => {
      try {
        if (videoRef?.current) {
          const stream = event.streams[0];
          const video = videoRef.current;
          
          // Wait for video to be ready before setting srcObject to avoid play() interruptions
          const setupStream = async () => {
            try {
              // Wait a bit for the video element to be ready
              if (video.readyState === 0) {
                await new Promise((resolve) => {
                  const onLoadedMetadata = () => {
                    video.removeEventListener("loadedmetadata", onLoadedMetadata);
                    resolve();
                  };
                  video.addEventListener("loadedmetadata", onLoadedMetadata);
                  // Fallback timeout
                  setTimeout(resolve, 100);
                });
              }
              
              // iOS Safari compatibility: Ensure video has required attributes
              // playsInline is critical for iOS to play video inline (not fullscreen)
              if (!video.hasAttribute("playsinline")) {
                video.setAttribute("playsinline", "true");
              }
              if (!video.hasAttribute("playsInline")) {
                video.setAttribute("playsInline", "true");
              }
              // iOS Safari requires webkit-playsinline for older iOS versions
              if (!video.hasAttribute("webkit-playsinline")) {
                video.setAttribute("webkit-playsinline", "true");
              }
              
              video.srcObject = stream;

              // Dispatch custom event when WebRTC stream starts
              try {
                window.dispatchEvent(
                  new CustomEvent("avatarStreamStart", {
                    detail: {
                      stream,
                      streamId,
                      timestamp: Date.now(),
                      hasVideoTrack: stream.getVideoTracks().length > 0,
                      hasAudioTrack: stream.getAudioTracks().length > 0,
                    },
                  })
                );
              } catch (dispatchErr) {
                console.warn("Failed to dispatch stream start event:", dispatchErr);
              }

              // Auto-unmute when WebRTC stream is attached (D-ID demo pattern)
              // Check if video has audio tracks before unmuting
              // Note: iOS Safari may require user interaction to unmute
              const hasAudioTrack = stream.getAudioTracks().length > 0;
              if (hasAudioTrack && video.muted) {
                video.muted = false;
                video.volume = 1;
              }
              
              // Try to play the video after srcObject is set
              // iOS Safari requires user interaction for autoplay, but we try anyway
              // Wait a bit more to ensure stream is ready
              setTimeout(() => {
                video.play().catch((err) => {
                  // AbortError is expected when video source changes, don't log it as error
                  if (err.name !== "AbortError") {
                    // iOS Safari often requires user interaction for autoplay
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                    if (isIOS) {
                      console.info(
                        "ℹ️ iOS detected: Video play may require user interaction. Error:",
                        err
                      );
                    } else {
                      console.warn(
                        "Auto-play failed (may require user interaction):",
                        err
                      );
                    }
                  }
                });
              }, 100);
            } catch (err) {
              console.error("Error setting up video stream:", err);
            }
          };
          
          setupStream();
        }
      } catch (err) {
        console.error("Video attach error:", err);
      }
    };

    /** Handle data channel for receiving messages from server */
    // The server may create the data channel, so we listen for it
    peerConnection.ondatachannel = (event) => {
      try {
        const dataChannel = event.channel;

        dataChannel.onopen = () => {};

        dataChannel.onclose = () => {};

        dataChannel.onerror = (error) => {
          console.error("❌ Data channel error:", error);
        };

        dataChannel.onmessage = (event) => {
          try {
            let message = event.data;
            
            // Handle different message types (string, JSON, ArrayBuffer)
            if (message instanceof ArrayBuffer) {
              message = new TextDecoder().decode(message);
            } else if (typeof message !== "string") {
              try {
                message = JSON.stringify(message);
              } catch {
                message = String(message);
              }
            }

            // Trim whitespace
            message = message.trim();

            // Check if message is "stream/done" (D-ID demo pattern)
            // The server sends "stream/done" or "stream/done:{json}" when the video stream is complete
            const isStreamDone =
              message === "stream/done" ||
              message.startsWith("stream/done");
              sessionStorage.setItem("isStreamDone", isStreamDone);
             

            if (isStreamDone) {
              // Try to parse JSON metadata if present
              let metadata = null;
              try {
                // Try parsing the entire message as JSON first
                if (message.startsWith("{")) {
                  metadata = JSON.parse(message);
                } else if (message.includes("{")) {
                  // Extract JSON part if message has prefix
                  const jsonPart = message.substring(message.indexOf("{"));
                  metadata = JSON.parse(jsonPart);
                } else if (message.includes(":")) {
                  // Handle "stream/done:jsonString" format
                  const colonIndex = message.indexOf(":");
                  if (colonIndex !== -1) {
                    const afterColon = message.substring(colonIndex + 1).trim();
                    if (afterColon.startsWith("{")) {
                      metadata = JSON.parse(afterColon);
                    }
                  }
                }
              } catch (parseErr) {
                console.warn("Could not parse stream/done metadata:", parseErr);
              }

              // Dispatch custom event to notify components that stream is done
              // This allows components to react (e.g., enable mic, hide loader, etc.)
              try {
                window.dispatchEvent(
                  new CustomEvent("avatarStreamDone", {
                    detail: {
                      message,
                      metadata,
                      timestamp: Date.now(),
                    },
                  })
                );
              } catch (dispatchErr) {
                console.warn(
                  "Failed to dispatch stream done event:",
                  dispatchErr
                );
              }
            } else {
              // Handle other message types - dispatch as generic data channel message
              try {
                let parsedMessage = message;
                try {
                  // Try to parse as JSON
                  parsedMessage = JSON.parse(message);
                } catch {
                  // Keep as string if not JSON
                }
                
                window.dispatchEvent(
                  new CustomEvent("avatarDataChannelMessage", {
                    detail: {
                      message: parsedMessage,
                      rawMessage: message,
                      timestamp: Date.now(),
                    },
                  })
                );
              } catch (dispatchErr) {
                console.warn("Failed to dispatch data channel message:", dispatchErr);
              }
            }
          } catch (err) {
            console.error("Error processing data channel message:", err);
          }
        };
      } catch (err) {
        console.error("Data channel setup error:", err);
      }
    };

    // Also try to create a data channel ourselves (in case server doesn't create it)
    // Some implementations require the client to create the data channel
    // Note: iOS Safari has limited data channel support but should work for basic messaging
    let dataChannel = null;
    try {
      // iOS Safari compatibility: Use ordered data channel with reliable delivery
      dataChannel = peerConnection.createDataChannel("messages", {
        ordered: true, // iOS Safari works better with ordered channels
        // maxRetransmits and maxPacketLifeTime may not be fully supported on iOS
      });

      dataChannel.onopen = () => {};

      dataChannel.onclose = () => {};

      dataChannel.onerror = (error) => {
        console.error("❌ Client data channel error:", error);
      };

      dataChannel.onmessage = (event) => {
        try {
          let message = event.data;
          
          // Handle different message types (string, JSON, ArrayBuffer)
          if (message instanceof ArrayBuffer) {
            message = new TextDecoder().decode(message);
          } else if (typeof message !== "string") {
            try {
              message = JSON.stringify(message);
            } catch {
              message = String(message);
            }
          }

          // Trim whitespace
          message = message.trim();

          // Check if message is "stream/done" (with or without JSON metadata)
          const isStreamDone =
            message === "stream/done" ||
            message.startsWith("stream/done");

          if (isStreamDone) {
            // Try to parse JSON metadata if present
            let metadata = null;
            try {
              // Try parsing the entire message as JSON first
              if (message.startsWith("{")) {
                metadata = JSON.parse(message);
              } else if (message.includes("{")) {
                // Extract JSON part if message has prefix
                const jsonPart = message.substring(message.indexOf("{"));
                metadata = JSON.parse(jsonPart);
              } else if (message.includes(":")) {
                // Handle "stream/done:jsonString" format
                const colonIndex = message.indexOf(":");
                if (colonIndex !== -1) {
                  const afterColon = message.substring(colonIndex + 1).trim();
                  if (afterColon.startsWith("{")) {
                    metadata = JSON.parse(afterColon);
                  }
                }
              }
            } catch (parseErr) {
              console.warn("Could not parse stream/done metadata:", parseErr);
            }

            try {
              window.dispatchEvent(
                new CustomEvent("avatarStreamDone", {
                  detail: {
                    message,
                    metadata,
                    timestamp: Date.now(),
                  },
                })
              );
            } catch (dispatchErr) {
              console.warn(
                "Failed to dispatch stream done event:",
                dispatchErr
              );
            }
          } else {
            // Handle other message types - dispatch as generic data channel message
            try {
              let parsedMessage = message;
              try {
                // Try to parse as JSON
                parsedMessage = JSON.parse(message);
              } catch {
                // Keep as string if not JSON
              }
              
              window.dispatchEvent(
                new CustomEvent("avatarDataChannelMessage", {
                  detail: {
                    message: parsedMessage,
                    rawMessage: message,
                    timestamp: Date.now(),
                  },
                })
              );
            } catch (dispatchErr) {
              console.warn("Failed to dispatch data channel message:", dispatchErr);
            }
          }
        } catch (err) {
          console.error("Error processing client data channel message:", err);
        }
      };
    } catch (err) {
      console.warn(
        "⚠️ Could not create client-side data channel (server may create it):",
        err
      );
    }

    /** ICE Candidates - Queue before SDP submission, flush after */
    peerConnection.onicecandidate = async (event) => {
      try {
        // Stop sending ICE candidates if we've had too many failures
        if (shouldStopSendingIceCandidates) {
          return;
        }

        if (event.candidate) {
          // Queue candidates if SDP not yet submitted
          if (!sdpSubmitted) {
            iceCandidateQueue.push(event.candidate);
          } else {
            // Send immediately if SDP already submitted
            await sendIceCandidate(event.candidate);
          }
        } else {
          // End of ICE candidates
          iceGatheringComplete = true;
          if (sdpSubmitted && !shouldStopSendingIceCandidates) {
            // Signal end-of-candidates to server
            await sendIceCandidate(null);
          }
        }
      } catch (err) {
        console.error("ICE submit error:", err);
      }
    };

    /** Monitor connection state changes */
    peerConnection.onconnectionstatechange = () => {
      try {
        const state = peerConnection.connectionState;

        if (state === "connected") {
          // console.log("✅ WebRTC connected successfully");
        } else if (state === "failed" || state === "disconnected") {
          console.warn("⚠️ WebRTC disconnected. State:", state);
          cleanup();
          // Note: Auto-reconnect removed to prevent infinite loops
          // Call connectAvatar() manually when needed
        }
      } catch (err) {
        console.error("Connection state error:", err);
      }
    };

    /** Monitor ICE connection state */
    peerConnection.oniceconnectionstatechange = () => {
      try {
        const state = peerConnection.iceConnectionState;

        if (state === "failed" || state === "closed") {
          console.error("❌ ICE connection failed/closed");
          cleanup();
        } else if (state === "connected" || state === "completed") {
          // console.log("✅ ICE connection established");
        }
      } catch (err) {
        console.error("ICE connection state error:", err);
      }
    };

    /** Set remote offer */
    try {
      await peerConnection.setRemoteDescription(offer);
    } catch (err) {
      throw new Error("Failed to set remote description");
    }

    /** Create SDP answer with iOS Safari compatibility */
    let answer;
    try {
      // iOS Safari requires specific SDP format (unified-plan)
      const answerOptions = {
        // Ensure unified-plan SDP for iOS Safari compatibility
        // This is the default in modern browsers, but explicitly set for iOS
      };
      
      answer = await peerConnection.createAnswer(answerOptions);
      
      // iOS Safari may need SDP modification for better compatibility
      // Ensure the SDP uses unified-plan format
      if (answer.sdp) {
        // Replace plan-b with unified-plan if needed (iOS Safari requirement)
        answer.sdp = answer.sdp.replace(/a=group:BUNDLE/g, 'a=group:BUNDLE');
        // Ensure proper codec ordering for iOS
        answer.sdp = answer.sdp.replace(/a=rtpmap:(\d+) (.*)/g, (match, id, codec) => {
          // Prioritize H.264 and VP8 for iOS compatibility
          return match;
        });
      }
      
      await peerConnection.setLocalDescription(answer);
    } catch (err) {
      throw new Error("Failed to create/set SDP answer");
    }

    /** Submit SDP */
    try {
      const sdpResponse = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submit_sdp",
          streamId: sessionId || streamId, // Use sessionId if available
          answer,
        }),
      });

      if (!sdpResponse.ok) {
        let errorText = "";
        try {
          const errorData = await sdpResponse.json();
          errorText = errorData.error || errorData.message || JSON.stringify(errorData);
        } catch {
          errorText = await sdpResponse.text().catch(() => `HTTP ${sdpResponse.status}`);
        }
        console.error("❌ Submit SDP API error:", sdpResponse.status, errorText);
        throw new Error(`Submit SDP failed: ${sdpResponse.status} - ${errorText}`);
      }
      
      sdpSubmitted = true;
    } catch (err) {
      console.error("❌ Submit SDP error:", err);
      throw new Error("Submit SDP API failed: " + err.message);
    }

    /** Flush queued ICE candidates after SDP submission */
    for (const candidate of iceCandidateQueue) {
      try {
        if (shouldStopSendingIceCandidates) {
          break; // Stop flushing if we've hit the failure limit
        }
        await sendIceCandidate(candidate);
      } catch (err) {
        console.warn("Failed to send queued ICE candidate:", err);
      }
    }
    iceCandidateQueue = [];

    /** Signal end-of-candidates if gathering already completed */
    if (iceGatheringComplete && !shouldStopSendingIceCandidates) {
      await sendIceCandidate(null);
    }

    // Log data channels after a short delay to see if they're created
    setTimeout(() => {}, 2000);

    return { peerConnection, streamId };
  } catch (error) {
    console.error("connectAvatar error:", error);
    cleanup();
    throw error;
  } finally {
    isConnecting = false;
  }
}
/**
 * Send ICE Candidate
 * @param {RTCIceCandidate|null} candidate - ICE candidate or null for end-of-candidates
 */
async function sendIceCandidate(candidate) {
  try {
    // Stop sending if flag is set
    if (shouldStopSendingIceCandidates) {
      return;
    }

    // Check connection state before sending
    if (peerConnection) {
      const state = peerConnection.connectionState;
      if (state === "closed" || state === "failed") {
        shouldStopSendingIceCandidates = true;
        return;
      }
    }

    if (!streamId && !sessionId) return;

    const payload = {
      action: "submit_ice",
      streamId: sessionId || streamId, // Use sessionId if available
    };

    if (candidate) {
      // Send actual candidate - ensure proper format
      // Some APIs expect the full candidate string, others expect individual fields
      // We'll send both formats to ensure compatibility
      if (candidate.candidate) {
        payload.candidate = candidate.candidate;
      } else if (typeof candidate === "string") {
        payload.candidate = candidate;
      } else {
        console.warn("⚠️ Invalid candidate format:", candidate);
        return;
      }
      
      // Include SDP fields if available
      if (candidate.sdpMid !== null && candidate.sdpMid !== undefined) {
        payload.sdpMid = candidate.sdpMid;
      }
      if (candidate.sdpMLineIndex !== null && candidate.sdpMLineIndex !== undefined) {
        payload.sdpMLineIndex = candidate.sdpMLineIndex;
      }
    } else {
      // Signal end-of-candidates
      payload.candidate = null;
    }

    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorText = "";
      try {
        const errorData = await response.json();
        errorText = errorData.error || errorData.message || JSON.stringify(errorData);
      } catch {
        errorText = await response.text().catch(() => `HTTP ${response.status}`);
      }
      console.error("❌ Submit ICE candidate error:", response.status, errorText);
      
      // Stop sending ICE candidates if we get 400 (Bad Request) errors
      // This indicates the connection/streamId is invalid
      if (response.status === 400) {
        iceCandidateFailureCount++;
        if (iceCandidateFailureCount >= maxIceCandidateFailures) {
          console.warn("⚠️ Too many ICE candidate failures (400 errors). Stopping ICE candidate sending.");
          shouldStopSendingIceCandidates = true;
          // Clear the ICE candidate handler to prevent further calls
          if (peerConnection) {
            peerConnection.onicecandidate = null;
          }
        }
      } else {
        // For other errors, increment counter but allow more attempts
        iceCandidateFailureCount++;
        if (iceCandidateFailureCount >= maxIceCandidateFailures * 2) {
          console.warn("⚠️ Too many ICE candidate failures. Stopping ICE candidate sending.");
          shouldStopSendingIceCandidates = true;
          if (peerConnection) {
            peerConnection.onicecandidate = null;
          }
        }
      }
      // Don't throw here - ICE candidate failures are often non-critical
    } else {
      // Reset failure count on success
      iceCandidateFailureCount = 0;
    }
  } catch (error) {
    console.error("❌ sendIceCandidate error:", error);
    iceCandidateFailureCount++;
    if (iceCandidateFailureCount >= maxIceCandidateFailures) {
      console.warn("⚠️ Too many ICE candidate errors. Stopping ICE candidate sending.");
      shouldStopSendingIceCandidates = true;
      if (peerConnection) {
        peerConnection.onicecandidate = null;
      }
    }
    // Don't throw - ICE candidate failures are often non-critical
  }
}
/**
 * Send Text Message
 */
export async function sendText(text) {
  try {
    if (!streamId && !sessionId) {
      console.error("❌ sendText: No streamId or sessionId available");
      return null;
    }
   
    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "send_content",
        streamId: sessionId || streamId, // Use sessionId if available
        contentType: "text",
        text,
      }),
    });

    // Check if response is OK before processing
    if (!response.ok) {
      let errorText = "";
      try {
        const errorData = await response.json();
        errorText = errorData.error || errorData.message || JSON.stringify(errorData);
      } catch {
        errorText = await response.text().catch(() => `HTTP ${response.status}`);
      }
      console.error("❌ Send text API error:", response.status, errorText);
      throw new Error(`Send text failed: ${response.status} - ${errorText}`);
    }

    const contentType = response.headers.get("content-type") || "";

    // If backend streams back a video file
    if (contentType.startsWith("video/")) {
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }

    // If backend responds with JSON containing a video URL
    if (contentType.includes("application/json")) {
      return await response.json();
    }

    // Fallback: plain text response
    const textResponse = await response.text();
    return textResponse || null;
  } catch (error) {
    console.error("❌ sendText error:", error);
    return null;
  }
}

/**
 * Cleanup connection
 */
function cleanup() {
  try {
    if (peerConnection) {
      // Remove event handlers to prevent memory leaks
      peerConnection.onicecandidate = null;
      peerConnection.ontrack = null;
      peerConnection.onconnectionstatechange = null;
      peerConnection.oniceconnectionstatechange = null;
      peerConnection.close();
      peerConnection = null;
    }
    streamId = null;
    sessionId = null; // Clear sessionId on cleanup
    iceCandidateQueue = [];
    sdpSubmitted = false;
    iceGatheringComplete = false;
    // Reset ICE candidate failure tracking
    iceCandidateFailureCount = 0;
    shouldStopSendingIceCandidates = false;
  } catch (error) {
    console.error("Cleanup error:", error);
  }
}

export function resetAvatarConnection() {
  try {
    cleanup();
    isConnecting = false;
    pendingConnectPromise = null;
    console.warn("♻️ Avatar connection reset");
  } catch (err) {
    console.error("Reset error:", err);
  }
}
